/**
 * BarcodeScannerModal — Live-Kamera-Scanner für EAN/QR.
 *
 * Nutzt @zxing/browser → unterstützt EAN-13, EAN-8, Code128, QR-Code uvm.
 * Workflow:
 *   1. Kamera anfordern (back-camera bevorzugt auf Mobile)
 *   2. Live-Decode kontinuierlich, bei Treffer → API-Lookup
 *   3. Artikel-Info + Quick-Buchung (Zugang/Abgang/Inventur)
 *   4. Buchung schickt POST /articles/{id}/movements
 *
 * Bei nicht gefundenem Barcode: Option „Neuen Artikel anlegen" mit
 * vorgefülltem EAN-Feld.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { toast } from "sonner";
import { X, ScanLine, Package, CheckCircle2, AlertTriangle, Plus, Camera, RefreshCw, ListChecks, ClipboardCheck, Minus } from "lucide-react";

import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtNum = (v) => Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export default function BarcodeScannerModal({ onClose, onBooked, onCreateUnknown, initialMode = "single" }) {
    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const controlsRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState("");
    const [lastCode, setLastCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [article, setArticle] = useState(null);
    const [error, setError] = useState("");
    const [unknown, setUnknown] = useState(null); // ean string if not found
    // Mode
    const [mode, setMode] = useState(initialMode); // "single" | "session"
    // Inventory session state: Map<articleId, {article, count}>
    const [sessionCounts, setSessionCounts] = useState({});
    const [sessionRef, setSessionRef] = useState("");
    const [reviewOpen, setReviewOpen] = useState(false);
    const [recentScan, setRecentScan] = useState(null); // {article, newCount} ephemeral
    const [employee, setEmployee] = useState("");
    // Booking form (single mode)
    const [movType, setMovType] = useState("in");
    const [qty, setQty] = useState("1");
    const [newQty, setNewQty] = useState("");
    const [unitCost, setUnitCost] = useState("");
    const [reference, setReference] = useState("");
    // Rundgang-Fokus im Inventur-Modus (Jun-2026): all | own | consignment
    const [walkMode, setWalkMode] = useState("all");

    const lookupArticle = useCallback(async (code) => {
        if (busy) return;
        setBusy(true);
        setError("");
        try {
            const res = await api.get(`/articles/by-ean/${encodeURIComponent(code)}`);
            const art = res.data;
            if (mode === "session") {
                // Rundgang-Fokus: Warnung bei falsch zugeordnetem Artikel (Jun-2026)
                if (walkMode !== "all") {
                    const isCons = art.ownership === "consignment";
                    const wantCons = walkMode === "consignment";
                    if (isCons !== wantCons) {
                        toast.warning(
                            `${art.sku}: ${isCons ? "Kommissionsware" : "Eigenbestand"} — aktueller Rundgang: ${wantCons ? "Kommissionsware" : "Eigenbestand"}`,
                            { description: "Trotzdem erfasst — bitte im passenden Rundgang zählen." },
                        );
                        if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
                    }
                }
                // Inventur-Modus: zähle +1 pro Scan, kein Modal-Wechsel
                setSessionCounts((prev) => {
                    const cur = prev[art.id] || { article: art, count: 0 };
                    const next = { ...prev, [art.id]: { article: art, count: cur.count + 1 } };
                    setRecentScan({ article: art, newCount: cur.count + 1 });
                    return next;
                });
                if (navigator.vibrate) navigator.vibrate(60);
                // ephemeral flash for 1.2s
                setTimeout(() => setRecentScan((cur) => cur?.article?.id === art.id ? null : cur), 1200);
            } else {
                setArticle(art);
                setUnknown(null);
                if (navigator.vibrate) navigator.vibrate(80);
            }
        } catch (e) {
            if (e.response?.status === 404) {
                if (mode === "session") {
                    toast.error(`Unbekannter Barcode: ${code}`);
                } else {
                    setUnknown(code);
                    setArticle(null);
                }
                if (navigator.vibrate) navigator.vibrate([60, 50, 60]);
            } else {
                setError(e.response?.data?.detail || "Lookup-Fehler");
            }
        }
        setBusy(false);
    }, [busy, mode, walkMode]);

    // Debounce repeated decodes — but in session mode allow re-scan of same code after cooldown
    const lastScanRef = useRef({ code: "", at: 0 });
    const handleDecode = useCallback((code) => {
        const now = Date.now();
        const prev = lastScanRef.current;
        const cooldown = mode === "session" ? 1200 : 3000;
        if (prev.code === code && (now - prev.at) < cooldown) return;
        // In single mode also block while a result is shown
        if (mode === "single" && (article || unknown)) return;
        lastScanRef.current = { code, at: now };
        setLastCode(code);
        lookupArticle(code);
    }, [mode, article, unknown, lookupArticle]);

    // Init camera + scanner
    useEffect(() => {
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;
        let cancelled = false;
        (async () => {
            try {
                const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
                if (cancelled) return;
                setDevices(videoInputDevices);
                // Prefer back camera
                const back = videoInputDevices.find((d) => /back|rear|environment/i.test(d.label));
                const chosen = back?.deviceId || videoInputDevices[0]?.deviceId || "";
                setDeviceId(chosen);
            } catch (e) {
                setError("Kamera-Zugriff fehlgeschlagen: " + (e?.message || "unbekannt"));
            }
        })();
        return () => {
            cancelled = true;
            try { controlsRef.current?.stop(); } catch { /* noop */ }
        };
    }, []);

    // Start decoding when device selected
    useEffect(() => {
        if (!deviceId || !videoRef.current || !readerRef.current) return;
        let active = true;
        (async () => {
            try {
                try { controlsRef.current?.stop(); } catch { /* noop */ }
                const controls = await readerRef.current.decodeFromVideoDevice(
                    deviceId,
                    videoRef.current,
                    (result, err) => {
                        if (!active) return;
                        if (result) {
                            handleDecode(result.getText());
                        }
                    },
                );
                controlsRef.current = controls;
            } catch (e) {
                setError("Scanner-Fehler: " + (e?.message || "unbekannt"));
            }
        })();
        return () => { active = false; };
    }, [deviceId, handleDecode]);

    const book = async () => {
        if (!article) return;
        const payload = {
            movement_type: movType,
            quantity: Number(qty) || 0,
            reference: reference || `Scan ${new Date().toLocaleString("de-DE")}`,
        };
        if (movType === "inventory") {
            if (newQty === "") { toast.error("Neuen Bestand eingeben"); return; }
            payload.new_qty = Number(newQty);
            payload.quantity = 0;
        }
        if (movType === "in" && unitCost !== "") payload.unit_cost_net = Number(unitCost);
        if ((movType === "in" || movType === "out") && (!Number(qty) || Number(qty) <= 0)) {
            toast.error("Menge > 0"); return;
        }
        setBusy(true);
        try {
            const res = await api.post(`/articles/${article.id}/movements`, payload);
            toast.success(`${article.sku}: Bestand → ${fmtNum(res.data.qty_after)} ${article.unit || "Stück"}`);
            if (navigator.vibrate) navigator.vibrate([40, 30, 40, 30, 40]);
            // Reset for next scan
            setArticle(null); setLastCode(""); setQty("1"); setNewQty(""); setUnitCost("");
            lastScanRef.current = { code: "", at: 0 };
            onBooked?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen"); }
        setBusy(false);
    };

    // -- Session-Mode helpers --
    const sessionItems = Object.values(sessionCounts);
    const sessionTotalScans = sessionItems.reduce((s, x) => s + x.count, 0);

    const adjustSessionCount = (articleId, delta) => {
        setSessionCounts((prev) => {
            const cur = prev[articleId];
            if (!cur) return prev;
            const next = Math.max(0, cur.count + delta);
            if (next === 0) {
                const { [articleId]: _drop, ...rest } = prev;
                return rest;
            }
            return { ...prev, [articleId]: { ...cur, count: next } };
        });
    };

    const setSessionCount = (articleId, value) => {
        const n = Math.max(0, Number(value) || 0);
        setSessionCounts((prev) => {
            const cur = prev[articleId];
            if (!cur) return prev;
            if (n === 0) {
                const { [articleId]: _drop, ...rest } = prev;
                return rest;
            }
            return { ...prev, [articleId]: { ...cur, count: n } };
        });
    };

    const commitSession = async () => {
        if (sessionItems.length === 0) { toast.error("Noch keine Artikel gescannt"); return; }
        const items = sessionItems.map((x) => ({ article_id: x.article.id, counted_qty: x.count }));
        setBusy(true);
        try {
            const res = await api.post("/articles/inventory-session", {
                reference: sessionRef || `Inventur ${new Date().toLocaleString("de-DE")}`,
                note: `Scanner-Erfassung · ${sessionItems.length} Artikel · ${sessionTotalScans} Scans`,
                employee: employee || null,
                method: "Scanner-Erfassung (Live-Kamera)",
                items,
            });
            const sid = res.data?.session_id;
            const valDiff = res.data?.total_value_diff || 0;
            toast.success(`Inventur gebucht: ${res.data.booked} Artikel · Wert-Δ ${fmtEur(valDiff)}`, {
                action: sid ? {
                    label: "PDF",
                    onClick: () => downloadInventoryPdf(sid, sessionRef),
                } : undefined,
                duration: 6000,
            });
            // Auto-Download des Belegs
            if (sid) {
                try { await downloadInventoryPdf(sid, sessionRef); } catch { /* user can retry via toast */ }
            }
            if (navigator.vibrate) navigator.vibrate([60, 40, 60, 40, 120]);
            setSessionCounts({});
            setSessionRef("");
            setEmployee("");
            setReviewOpen(false);
            lastScanRef.current = { code: "", at: 0 };
            onBooked?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Inventur-Buchung fehlgeschlagen"); }
        setBusy(false);
    };

    const downloadInventoryPdf = async (sid, refName) => {
        const res = await api.get(`/inventory-sessions/${sid}/pdf`, { responseType: "blob" });
        previewPdfResponse(res, `Inventur_${(refName || sid).replace(/[\s/]/g, "_")}.pdf`);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4" data-testid="scanner-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-3 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h2 className="font-display font-black text-lg inline-flex items-center gap-2">
                        <ScanLine className="w-5 h-5" />
                        Barcode-Scanner
                    </h2>
                    <div className="flex items-center gap-2">
                        {mode === "session" && sessionItems.length > 0 && !reviewOpen && (
                            <button
                                type="button"
                                onClick={() => setReviewOpen(true)}
                                className="text-[11px] font-bold uppercase tracking-wider bg-[#FFCC00] text-[#111827] px-2 py-1 rounded-sm inline-flex items-center gap-1"
                                data-testid="scanner-review-btn"
                            >
                                <ListChecks className="w-3.5 h-3.5" />
                                {sessionItems.length}/{sessionTotalScans}
                            </button>
                        )}
                        <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="scanner-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="px-3 pt-2">
                    <div className="flex gap-1.5 bg-gray-100 rounded-sm p-1" data-testid="scanner-mode-toggle">
                        <button
                            type="button"
                            onClick={() => { setMode("single"); setReviewOpen(false); }}
                            className={`flex-1 text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-sm transition-colors ${mode === "single" ? "bg-white text-[#111827] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                            data-testid="scanner-mode-single"
                        >
                            Einzel-Buchung
                        </button>
                        <button
                            type="button"
                            onClick={() => { setMode("session"); setArticle(null); setUnknown(null); }}
                            className={`flex-1 text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-sm transition-colors ${mode === "session" ? "bg-[#FFCC00] text-[#111827] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                            data-testid="scanner-mode-session"
                        >
                            Inventur-Modus
                        </button>
                    </div>
                </div>

                {mode === "session" && !reviewOpen && (
                    <div className="px-3 pt-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold shrink-0">Rundgang</span>
                            <div className="flex gap-1 bg-gray-100 rounded-sm p-1 flex-1" data-testid="walk-mode-toggle">
                                {[["all", "Alle"], ["own", "Eigenbestand"], ["consignment", "Kommission"]].map(([val, label]) => (
                                    <button
                                        key={val}
                                        type="button"
                                        onClick={() => setWalkMode(val)}
                                        className={`flex-1 text-[10px] font-bold uppercase tracking-wider py-1 rounded-sm transition-colors ${walkMode === val ? (val === "consignment" ? "bg-purple-600 text-white shadow-sm" : "bg-white text-[#111827] shadow-sm") : "text-gray-500 hover:text-gray-700"}`}
                                        data-testid={`walk-mode-${val}`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {walkMode !== "all" && (
                            <p className="text-[10px] text-gray-500 mt-1">
                                Fokus auf <strong>{walkMode === "consignment" ? "Kommissionsware" : "Eigenbestand"}</strong> — beim Scannen abweichender Ware erscheint ein Hinweis.
                            </p>
                        )}
                    </div>
                )}

                {reviewOpen ? (
                    <SessionReview
                        items={sessionItems}
                        sessionRef={sessionRef}
                        setSessionRef={setSessionRef}
                        employee={employee}
                        setEmployee={setEmployee}
                        onAdjust={adjustSessionCount}
                        onSet={setSessionCount}
                        onBack={() => setReviewOpen(false)}
                        onCommit={commitSession}
                        busy={busy}
                        totalScans={sessionTotalScans}
                    />
                ) : (
                <div className="p-3 space-y-3">
                    {/* Camera selector */}
                    {devices.length > 1 && (
                        <div className="flex items-center gap-2">
                            <Camera className="w-4 h-4 text-gray-500" />
                            <Select value={deviceId} onValueChange={setDeviceId}>
                                <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="scanner-device"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {devices.map((d) => (
                                        <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `Kamera ${d.deviceId.slice(0, 6)}`}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Video preview */}
                    <div className="relative bg-black rounded-sm overflow-hidden aspect-video" data-testid="scanner-video-wrap">
                        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                        <div className="absolute inset-x-0 top-1/2 h-0.5 bg-red-500/80 shadow-lg shadow-red-500/50 pointer-events-none" />
                        <div className={`absolute inset-x-8 inset-y-12 border-2 rounded-sm pointer-events-none transition-colors ${mode === "session" ? "border-[#FFCC00]/80" : "border-[#FFCC00]/60"}`} />
                        {lastCode && !article && !unknown && mode === "single" && (
                            <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm font-mono-num">
                                Erkannt: {lastCode}
                            </div>
                        )}
                        {/* Session-Modus: flash overlay */}
                        {mode === "session" && recentScan && (
                            <div className="absolute inset-0 bg-[#FFCC00]/40 flex items-center justify-center animate-pulse" data-testid="session-flash">
                                <div className="bg-white rounded-sm px-4 py-2 text-center">
                                    <div className="font-mono-num text-[10px] text-gray-500">{recentScan.article.sku}</div>
                                    <div className="font-bold text-sm">{recentScan.article.name}</div>
                                    <div className="font-display font-black text-2xl text-[#111827]">
                                        × {recentScan.newCount}
                                    </div>
                                    {recentScan.article.committed_in_orders > 0 && (
                                        <div className="text-[10px] text-amber-800 mt-0.5">{fmtNum(recentScan.article.committed_in_orders)} in Aufträgen</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-2 text-xs text-red-900 rounded-sm" data-testid="scanner-error">
                            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {error}
                        </div>
                    )}

                    {/* Session info bar */}
                    {mode === "session" && (
                        <div className="bg-[#FFCC00]/10 border border-[#FFCC00]/40 rounded-sm p-2 flex items-center justify-between" data-testid="session-info">
                            <div className="text-xs text-gray-700">
                                <div className="font-bold uppercase tracking-wider text-[10px]">Inventur läuft</div>
                                <div className="text-[11px] text-gray-600 mt-0.5">
                                    {sessionItems.length} Artikel · {sessionTotalScans} Scans
                                </div>
                            </div>
                            <Button
                                type="button"
                                onClick={() => setReviewOpen(true)}
                                disabled={sessionItems.length === 0}
                                className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold text-[11px] h-8"
                                data-testid="session-end-btn"
                            >
                                <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Erfassung beenden
                            </Button>
                        </div>
                    )}

                    {/* Detected article (single mode) */}
                    {mode === "single" && article && (
                        <div className="border-2 border-green-500 bg-green-50/50 rounded-sm p-3 space-y-2" data-testid="scanner-article">
                            <div className="flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono-num text-[11px] text-gray-500">{article.sku}</div>
                                    <div className="font-bold text-sm">{article.name}</div>
                                    <div className="text-[11px] text-gray-600 mt-0.5">
                                        Bestand: <strong className="font-mono-num text-base">{fmtNum(article.stock_qty)}</strong> {article.unit || "Stück"}
                                        {article.min_stock > 0 && <span className="text-gray-400"> · Min {fmtNum(article.min_stock)}</span>}
                                        {" · VK "} <strong>{fmtEur(article.sell_price_gross)}</strong>
                                    </div>
                                    {article.committed_in_orders > 0 && (
                                        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mt-1" data-testid="scanner-committed">
                                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                                            Davon <strong className="font-mono-num">{fmtNum(article.committed_in_orders)}</strong> {article.unit || "Stück"} in offenen Aufträgen gebunden — diese liegen evtl. nicht im Lager.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-green-200">
                                <Select value={movType} onValueChange={setMovType}>
                                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="scan-mov-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="in">Zugang</SelectItem>
                                        <SelectItem value="out">Abgang</SelectItem>
                                        <SelectItem value="inventory">Inventur</SelectItem>
                                    </SelectContent>
                                </Select>
                                {movType === "inventory" ? (
                                    <Input type="number" step="0.5" placeholder="Gezählt" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="rounded-sm h-8 text-sm" data-testid="scan-newqty" autoFocus />
                                ) : (
                                    <Input type="number" step="0.5" placeholder="Menge" value={qty} onChange={(e) => setQty(e.target.value)} className="rounded-sm h-8 text-sm" data-testid="scan-qty" autoFocus />
                                )}
                            </div>
                            {movType === "in" && (
                                <Input type="number" step="0.01" placeholder="Stk-Preis EK netto (optional)" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="rounded-sm h-8 text-sm" />
                            )}
                            <Input placeholder="Belegnummer (optional)" value={reference} onChange={(e) => setReference(e.target.value)} className="rounded-sm h-8 text-sm" />

                            <div className="grid grid-cols-2 gap-2">
                                <Button type="button" variant="outline" onClick={() => { setArticle(null); setLastCode(""); }} className="rounded-sm" data-testid="scan-skip">
                                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Nächster Scan
                                </Button>
                                <Button type="button" onClick={book} disabled={busy} className="rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider" data-testid="scan-book">
                                    {busy ? "…" : "Buchen"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Unknown code (single mode) */}
                    {mode === "single" && unknown && (
                        <div className="border-2 border-amber-500 bg-amber-50/50 rounded-sm p-3" data-testid="scanner-unknown">
                            <div className="flex items-start gap-2 mb-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <div className="font-bold">Kein Artikel mit Barcode <code className="font-mono-num bg-amber-100 px-1 rounded-sm">{unknown}</code></div>
                                    <div className="text-xs text-gray-600 mt-0.5">Sie können einen neuen Artikel mit diesem EAN anlegen.</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Button type="button" variant="outline" onClick={() => { setUnknown(null); setLastCode(""); }} className="rounded-sm" data-testid="scan-unknown-skip">
                                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Erneut scannen
                                </Button>
                                <Button type="button" onClick={() => onCreateUnknown?.(unknown)} className="rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid="scan-create">
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Anlegen
                                </Button>
                            </div>
                        </div>
                    )}

                    {!article && !unknown && (
                        <div className="text-xs text-gray-500 text-center py-1 flex items-center justify-center gap-1.5">
                            <Package className="w-3.5 h-3.5" />
                            {mode === "session"
                                ? "Jeder Scan zählt diesen Artikel +1. Identische Codes nach ~1 Sek. erneut scannbar."
                                : "Barcode in den gelben Rahmen halten — Erkennung läuft automatisch."}
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}

function GroupHeader({ label, count, diff, purple, testid }) {
    return (
        <div className={`px-2 py-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider sticky top-0 z-10 ${purple ? "bg-purple-50 text-purple-900 border-y border-purple-100" : "bg-gray-100 text-gray-700 border-y border-gray-200"}`} data-testid={testid}>
            <span>{label} · {count}</span>
            <span className={`font-mono-num ${diff < 0 ? "text-red-700" : diff > 0 ? "text-green-700" : ""}`}>Wert-Δ {fmtEur(diff)}</span>
        </div>
    );
}

function SessionReview({ items, sessionRef, setSessionRef, employee, setEmployee, onAdjust, onSet, onBack, onCommit, busy, totalScans }) {
    const changes = items.reduce((acc, x) => {
        const before = Number(x.article.stock_qty || 0);
        const diff = x.count - before;
        if (diff > 0) acc.up += 1;
        else if (diff < 0) acc.down += 1;
        else acc.eq += 1;
        return acc;
    }, { up: 0, down: 0, eq: 0 });

    // Live Wert-Δ Berechnung (Frontend-Schätzung; Server berechnet authoritative)
    const valueDiff = items.reduce((sum, x) => {
        const before = Number(x.article.stock_qty || 0);
        const diff = x.count - before;
        const cost = Number(x.article.purchase_price_net || 0);
        return sum + diff * cost;
    }, 0);

    // Getrennte Erfassung: Eigenbestand vs. Kommissionsware (Fremdeigentum)
    const consItems = items.filter((x) => x.article.ownership === "consignment");
    const ownItems = items.filter((x) => x.article.ownership !== "consignment");
    const hasCons = consItems.length > 0;
    const groupDiff = (arr) => arr.reduce((sum, x) => {
        const before = Number(x.article.stock_qty || 0);
        return sum + (x.count - before) * Number(x.article.purchase_price_net || 0);
    }, 0);
    const ownDiff = groupDiff(ownItems);
    const consDiff = groupDiff(consItems);

    const renderRow = ({ article, count }) => {
        const before = Number(article.stock_qty || 0);
        const diff = count - before;
        const isCons = article.ownership === "consignment";
        return (
            <div key={article.id} className="p-2 flex gap-2 items-center" data-testid={`session-item-${article.id}`}>
                <div className="min-w-0 flex-1">
                    <div className="font-mono-num text-[10px] text-gray-500 flex items-center gap-1">
                        {article.sku}
                        {isCons && <span className="text-[9px] uppercase tracking-wider text-purple-700 bg-purple-50 px-1 rounded-sm not-italic">Kommission</span>}
                    </div>
                    <div className="font-bold text-sm truncate">{article.name}</div>
                    <div className="text-[10px] text-gray-500">
                        aktuell {fmtNum(before)} → gezählt <strong className={diff > 0 ? "text-green-700" : diff < 0 ? "text-red-700" : ""}>
                            {fmtNum(count)}
                        </strong> · Δ {diff > 0 ? "+" : ""}{fmtNum(diff)}
                    </div>
                    {article.committed_in_orders > 0 && (
                        <div className="text-[10px] text-amber-800" data-testid={`session-committed-${article.id}`}>
                            davon {fmtNum(article.committed_in_orders)} in offenen Aufträgen gebunden
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => onAdjust(article.id, -1)}
                    className="w-7 h-7 rounded-sm border border-gray-300 hover:bg-red-50 hover:border-red-300 text-red-700 flex items-center justify-center"
                    data-testid={`session-minus-${article.id}`}
                >
                    <Minus className="w-3.5 h-3.5" />
                </button>
                <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={count}
                    onChange={(e) => onSet(article.id, e.target.value)}
                    className="w-16 rounded-sm h-7 text-sm text-center font-mono-num"
                    data-testid={`session-count-${article.id}`}
                />
                <button
                    type="button"
                    onClick={() => onAdjust(article.id, +1)}
                    className="w-7 h-7 rounded-sm border border-gray-300 hover:bg-green-50 hover:border-green-300 text-green-700 flex items-center justify-center"
                    data-testid={`session-plus-${article.id}`}
                >
                    <Plus className="w-3.5 h-3.5" />
                </button>
            </div>
        );
    };

    return (
        <div className="p-3 space-y-3" data-testid="session-review">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-sm text-xs text-amber-900">
                <div className="font-bold mb-1 flex items-center gap-1.5">
                    <ClipboardCheck className="w-4 h-4" /> Inventur abschließen
                </div>
                <div>Beim Buchen wird je Artikel <strong>ein Inventur-Beleg</strong> erstellt (movement_type=inventory, qty_after = gezählter Stand). Diff zum aktuellen Lagerbestand wird automatisch berechnet. Ein GoBD-konformer <strong>PDF-Beleg</strong> wird automatisch erzeugt und heruntergeladen.</div>
            </div>

            <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Artikel</div>
                    <div className="font-display font-black text-xl">{items.length}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Scans</div>
                    <div className="font-display font-black text-xl">{totalScans}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Veränderung</div>
                    <div className="text-xs flex justify-center gap-1 mt-1">
                        <span className="text-green-700 font-bold">+{changes.up}</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-red-700 font-bold">−{changes.down}</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-600 font-bold">={changes.eq}</span>
                    </div>
                </div>
                <div className={`border rounded-sm p-2 ${valueDiff < 0 ? "bg-red-50 border-red-200" : valueDiff > 0 ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Wert-Δ (EK)</div>
                    <div className={`font-display font-black text-base ${valueDiff < 0 ? "text-red-700" : valueDiff > 0 ? "text-green-700" : ""}`}>
                        {fmtEur(valueDiff)}
                    </div>
                </div>
            </div>

            {hasCons && (
                <div className="flex items-center justify-between text-[11px] bg-purple-50 border border-purple-200 rounded-sm px-3 py-1.5" data-testid="session-split-summary">
                    <span className="text-gray-700">Getrennte Erfassung:</span>
                    <span>Eigenbestand <strong className="font-mono-num">{fmtEur(ownDiff)}</strong></span>
                    <span className="text-purple-800">Kommission <strong className="font-mono-num">{fmtEur(consDiff)}</strong></span>
                </div>
            )}

            <div className="grid grid-cols-2 gap-2">
                <Input
                    value={sessionRef}
                    onChange={(e) => setSessionRef(e.target.value)}
                    placeholder="Belegnummer / Bezeichnung"
                    className="rounded-sm"
                    data-testid="session-ref"
                />
                <Input
                    value={employee}
                    onChange={(e) => setEmployee(e.target.value)}
                    placeholder="Mitarbeiter (Erfasser)"
                    className="rounded-sm"
                    data-testid="session-employee"
                />
            </div>

            <div className="max-h-[40vh] overflow-y-auto border border-gray-200 rounded-sm" data-testid="session-items">
                {hasCons ? (
                    <>
                        <GroupHeader label="Eigenbestand" count={ownItems.length} diff={ownDiff} testid="session-group-own" />
                        <div className="divide-y divide-gray-100">{ownItems.map(renderRow)}</div>
                        <GroupHeader label="Kommissionsware (Fremdeigentum)" count={consItems.length} diff={consDiff} purple testid="session-group-consignment" />
                        <div className="divide-y divide-gray-100">{consItems.map(renderRow)}</div>
                    </>
                ) : (
                    <div className="divide-y divide-gray-100">{items.map(renderRow)}</div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" onClick={onBack} className="rounded-sm" data-testid="session-back">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Weiter scannen
                </Button>
                <Button
                    type="button"
                    onClick={onCommit}
                    disabled={busy || items.length === 0}
                    className="rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider disabled:opacity-50"
                    data-testid="session-commit"
                >
                    {busy ? "Buche…" : "Inventur buchen"}
                </Button>
            </div>
        </div>
    );
}
